import React, { useEffect, useState } from 'react';
import usePageMetadata from '../utils/usePageMetadata';
import { apiUrl } from '../utils/api';
import './Auth.css';

const SimpleOAuthCallback = () => {
  usePageMetadata({
    title: 'Authenticating... | The Ad Shop',
    description: 'Processing your authentication request.',
    meta: [
      { name: 'robots', content: 'noindex, nofollow' },
    ],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('Processing authentication...');

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        console.log('Simple OAuth callback page loaded');
        
        // Clear any existing token before processing OAuth callback
        // This ensures we use the token from the new authentication
        const oldToken = localStorage.getItem('token');
        if (oldToken) {
          console.log('Clearing old token before processing OAuth callback');
          localStorage.removeItem('token');
        }
        
        // Get URL parameters manually
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const error = urlParams.get('error');

        console.log('OAuth callback params:', { code: !!code, state, error });

        if (error) {
          console.error('OAuth error:', error);
          setError('Google authentication was cancelled or failed.');
          setLoading(false);
          return;
        }

        if (!code) {
          console.error('No authorization code received');
          setError('No authorization code received from Google.');
          setLoading(false);
          return;
        }

        // Parse state to get siteId
        let siteId = null;
        try {
          const stateData = JSON.parse(state || '{}');
          siteId = stateData.siteId;
        } catch (e) {
          console.warn('Could not parse state:', e);
        }

        setStatus('Exchanging authorization code for tokens...');

        // Exchange code for tokens
        // Note: This endpoint doesn't require authentication since it's for login
        console.log('Exchanging code for tokens...');
        const headers = {
          'Content-Type': 'application/json'
        };
        
        // Only include Authorization header if token exists (for existing users adding GA4 to a site)
        const existingToken = localStorage.getItem('token');
        if (existingToken) {
          headers['Authorization'] = `Bearer ${existingToken}`;
        }
        
        const response = await fetch(apiUrl('/api/auth'), {
          method: 'POST',
          headers: headers,
          body: JSON.stringify({ 
            code, 
            siteId,
            path: '/google-oauth' // Tell the consolidated auth API which handler to use
          })
        });

        console.log('OAuth response status:', response.status);
        
        let data;
        try {
          data = await response.json();
        } catch (jsonError) {
          console.error('Failed to parse response as JSON:', jsonError);
          throw new Error('Invalid response from server. Please try again.');
        }
        
        console.log('OAuth response data:', data);

        if (!response.ok) {
          if (response.status === 403 && data.status === 'pending') {
            setStatus('Account pending approval...');
            setLoading(false);
            const pendingEmail = data.user?.email || '';
            const encodedEmail = pendingEmail ? `?email=${encodeURIComponent(pendingEmail)}` : '';
            setTimeout(() => {
              window.location.replace(`/waitlist${encodedEmail}`);
            }, 1500);
            return;
          }
          // Handle specific error codes
          if (data.code === 'EMAIL_ALREADY_LINKED') {
            const errorMessage = 'This email is already associated with a different Google account. Please use the original Google account or use email/password login.';
            console.error('OAuth authentication failed - email already linked:', errorMessage);
            setError(errorMessage);
            setLoading(false);
            return;
          }
          
          const errorMessage = data.message || data.error || `Authentication failed (${response.status})`;
          console.error('OAuth authentication failed:', errorMessage);
          throw new Error(errorMessage);
        }

        setStatus('Authentication successful! Redirecting...');

        // Update stored token if new one provided
        if (data.token) {
          localStorage.setItem('token', data.token);
        } else if (data.requiresApproval) {
          setLoading(false);
          setStatus('Account pending approval...');
          const pendingEmail = data.user?.email || '';
          const encodedEmail = pendingEmail ? `?email=${encodeURIComponent(pendingEmail)}` : '';
          setTimeout(() => {
            window.location.replace(`/waitlist${encodedEmail}`);
          }, 1500);
          return;
        }

        // If we have a siteId, fetch properties and redirect to analytics
        if (siteId) {
          try {
            setStatus('Connecting to Google Analytics...');
            
            const propertiesResponse = await fetch(apiUrl('/api/analytics'), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              },
              body: JSON.stringify({
                siteId: siteId,
                path: '/properties' // Tell the consolidated analytics API which handler to use
              })
            });

            const propertiesData = await propertiesResponse.json();

            if (propertiesResponse.ok && propertiesData.properties && propertiesData.properties.length > 0) {
              setStatus('Connecting to your first property...');
              
              // Auto-connect the first property
              const connectResponse = await fetch(apiUrl('/api/sites'), {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                  siteId: siteId,
                  path: `/${siteId}/analytics`, // Tell the consolidated sites API which handler to use
                  propertyId: propertiesData.properties[0].propertyId,
                  propertyName: propertiesData.properties[0].propertyName
                })
              });

              if (connectResponse.ok) {
                setStatus('Success! Redirecting to sites...');
                // Redirect to sites page to stay in onboarding flow
                setTimeout(() => {
                  window.location.href = '/sites';
                }, 1000);
                return;
              }
            }
          } catch (propertiesError) {
            console.error('Error connecting property:', propertiesError);
          }
        }

        // Always redirect to sites page to keep users in onboarding flow
        setStatus('Redirecting to sites...');
        setTimeout(() => {
          window.location.href = '/sites';
        }, 1000);
      } catch (error) {
        console.error('OAuth callback error:', error);
        setError(error.message || 'Authentication failed. Please try again.');
        setLoading(false);
      }
    };

    handleOAuthCallback();
  }, []);

  if (loading) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <h2>Connecting to Google Analytics...</h2>
            <p>{status}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="error-container">
            <h2>Connection Failed</h2>
            <p>{error}</p>
            <button 
              className="btn-primary" 
              onClick={() => window.location.href = '/sites'}
            >
              Return to Sites
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default SimpleOAuthCallback;